import { useCallback, useRef, useState } from 'react';
import { BraindayAPI, BraindayError } from '@/services/BraindayAPI';
import type {
  CommandInput,
  CommandOutput,
  CompressInput,
  CompressOutput,
  MicroStepInput,
  MicroStepOutput,
  ScheduleInput,
  ScheduleOutput,
} from '@/lib/aiClient';

interface AsyncHookState<TData> {
  data: TData | null;
  loading: boolean;
  error: BraindayError | null;
}

function useAsyncCall<TInput, TData>(
  fn: (input: TInput) => Promise<TData>,
): AsyncHookState<TData> & { run: (input: TInput) => Promise<TData>; reset: () => void } {
  const [data, setData] = useState<TData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<BraindayError | null>(null);
  const callIdRef = useRef(0);

  const run = useCallback(
    async (input: TInput) => {
      const myId = ++callIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const result = await fn(input);
        if (callIdRef.current === myId) {
          setData(result);
          setLoading(false);
        }
        return result;
      } catch (e) {
        const err = e instanceof BraindayError ? e : new BraindayError('unknown', String(e));
        if (callIdRef.current === myId) {
          setError(err);
          setLoading(false);
        }
        throw err;
      }
    },
    [fn],
  );

  const reset = useCallback(() => {
    callIdRef.current++;
    setData(null);
    setLoading(false);
    setError(null);
  }, []);

  return { data, loading, error, run, reset };
}

export function useAiSchedule() {
  return useAsyncCall<ScheduleInput, ScheduleOutput>(BraindayAPI.schedule);
}

export function useAiMicroStep() {
  return useAsyncCall<MicroStepInput, MicroStepOutput>(BraindayAPI.microStep);
}

export function useAiCompress() {
  return useAsyncCall<CompressInput, CompressOutput>(BraindayAPI.compress);
}

// useAiCommand has a different shape (streaming) — implemented in useCommand.ts (Task 10).
export type { CommandInput, CommandOutput };
